import React, { useState } from "react";
import { useSelector } from "react-redux";
import { selectCurrentUser } from "../../store/slices/currentUser";
import {
  PersonOutline,
  LockOpen,
  AttachMoney,
  NotificationsNone,
  MenuBook,
} from "@mui/icons-material";
import { Box, Card, CardContent, Grid, Tab, Tabs } from "@mui/material";
import TeacherData from "../../components/teacher-data";
import TeacherDataSkeleton from "../../components/teacher-data/skeleton";
import TeacherSecurity from "../../components/teacher-security";
import TeacherTickets from "../../components/teacher-tickets";
import TeacherTicketsSummary from "../../components/teacher-tickets-summary";
import TeacherPaymentsList from "../../components/teacher-payments";
import TeacherCoursesSummary from "../../components/teacher-courses-summary";
import TeacherCourses from "../../components/teacher-courses";
import TeacherPaymentsSummary from "../../components/teacher-payments-summary";
import TeacherTabSummary from "../../components/teacher-tab-summary";
import { useLocation } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

const isLoading = false;

export default function TeachersPage() {
  const location = useLocation();
  const state = location.state;
  const { userId, businessId, clientId, roleId } = useSelector(selectCurrentUser);
  const [value, setValue] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const handleChange = (_, newValue) => setValue(newValue);

  return (
    <Box sx={{ width: "100%", overflowX: "hidden" }}>
      <Grid
        container
        pt="0px"
        spacing={2}
        sx={{
          height: "auto",
          marginBottom: 3,
          marginTop: 0,
          paddingBottom: 10,
        }}
      >
        {/* Left menu resume */}
        <Grid item xs={12} md={4} sx={{ minWidth: 0, maxWidth: "100%" }}>
          <Card
            elevation={3}
            align="center"
            sx={{ height: "auto", width: "100%", overflow: "hidden" }}
          >
            {!isLoading ? (
              <>
                {state ? (
                  <TeacherData
                    userId={state.userId}
                    clientId={state.clientId}
                  />
                ) : (
                  <TeacherData userId={userId} />
                )}
              </>
            ) : (
              <TeacherDataSkeleton />
            )}
          </Card>
        </Grid>

        {/* Right menu tabs*/}
        <Grid item xs={12} md={8} sx={{ minWidth: 0, maxWidth: "100%" }}>
          {!isLoading && (
            <Box sx={{ width: "100%", minWidth: 0 }}>
              <Box
                sx={{
                  backgroundColor: "white",
                  borderBottom: 1,
                  borderColor: "divider",
                  width: "100%",
                  maxWidth: { xs: "calc(100vw - 100px)", md: "100%" },
                  mb: 2,
                }}
              >
                <Tabs
                  value={value}
                  onChange={handleChange}
                  variant={isMobile ? "scrollable" : "standard"}
                  scrollButtons="auto" // Muestra los botones solo si es necesario
                  allowScrollButtonsMobile // Permite que se vean en dispositivos táctiles
                  sx={{ "& .MuiTabs-flexContainer": { justifyContent: { xs: "flex-start", md: "center" } } }}
                >
                  <Tab
                    icon={<PersonOutline />}
                    label="RESUMEN"
                    iconPosition="start"
                  />
                  <Tab
                    icon={<MenuBook />}
                    label="CURSOS"
                    iconPosition="start"
                  />
                  <Tab
                    icon={<AttachMoney />}
                    label="PAGOS"
                    iconPosition="start"
                  />
                  <Tab
                    icon={<NotificationsNone />}
                    label="COMUNICACIONES"
                    iconPosition="start"
                  />
                  <Tab
                    icon={<LockOpen />}
                    label="SEGURIDAD"
                    iconPosition="start"
                  />
                </Tabs>
              </Box>

              {/* SUMMARY */}
              {value === 0 && (
                <Box sx={{ overflowX: "hidden" }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      {state ? (
                        <TeacherCourses
                          businessId={state.teacherId}
                          isSummary={true}
                        />
                      ) : (
                        <TeacherCourses
                          businessId={businessId}
                          isSummary={true}
                        />
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      {state ? (
                        <TeacherTabSummary businessId={state.teacherId} />
                      ) : (
                        <TeacherTabSummary businessId={businessId} />
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      {state ? (
                        <TeacherPaymentsList
                          isSummary={true}
                          businessId={state.teacherId}
                          clientId={state.clientId}
                        />
                      ) : (
                        <TeacherPaymentsList
                          isSummary={true}
                          businessId={businessId}
                          clientId={clientId}
                        />
                      )}
                    </Grid>
                  </Grid>
                </Box>
              )}
              {/* COURSES */}
              {value === 1 && (
                <Box sx={{ overflowX: "hidden" }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      {state ? (
                        <TeacherCoursesSummary
                          businessId={state.teacherId}
                        />
                      ) : (
                        <TeacherCoursesSummary businessId={businessId} />
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      {state ? (
                        <TeacherCourses
                          businessId={state.teacherId}
                          isSummary={false}
                        />
                      ) : (
                        <TeacherCourses
                          businessId={businessId}
                          isSummary={false}
                        />
                      )}
                    </Grid>
                  </Grid>
                </Box>
              )}
              {/* PAYMENTS */}
              {value === 2 && (
                <Box sx={{ overflowX: "hidden" }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      {state ? (
                        <TeacherPaymentsSummary
                          businessId={state.teacherId}
                        />
                      ) : (
                        <TeacherPaymentsSummary businessId={businessId} />
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      {state ? (
                        <TeacherPaymentsList
                          isSummary={false}
                          businessId={state.teacherId}
                          clientId={state.clientId}
                          roleId={roleId}
                        />
                      ) : (
                        <TeacherPaymentsList
                          isSummary={false}
                          businessId={businessId}
                          clientId={clientId}
                          roleId={roleId}
                        />
                      )}
                    </Grid>
                  </Grid>
                </Box>
              )}
              {/* TICKETS */}
              {value === 3 && (
                <Box sx={{ overflowX: "hidden" }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      {state ? (
                        <TeacherTicketsSummary
                          businessId={state.teacherId}
                        />
                      ) : (
                        <TeacherTicketsSummary businessId={businessId} />
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      {state ? (
                        <TeacherTickets
                          businessId={state.teacherId}
                          clientId={state.clientId}
                        />
                      ) : (
                        <TeacherTickets
                          businessId={businessId}
                          clientId={clientId}
                        />
                      )}
                    </Grid>
                  </Grid>
                </Box>
              )}
              {/* SECURITY */}
              {value === 4 && (
                <Box sx={{ overflowX: "hidden" }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      {state ? (
                        <TeacherSecurity
                          userId={state.userId}
                          username={state.username}
                          roleId={roleId}
                        />
                      ) : (
                        <TeacherSecurity userId={userId} roleId={roleId} />
                      )}
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}